import { useState } from "react";

import { Loader2, Monitor, Pencil, Plus, Server, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { HostFormDialog } from "@/features/servers/components/HostFormDialog";
import { useHostMutations, useHosts } from "@/features/servers/hooks/useCenterInfra";
import {
  HOST_KIND_LABELS,
  type CreateHostInput,
  type IHost,
} from "@/features/servers/schemas/servers";
import {
  formatOsLabel,
  resolveHostOsVersion,
} from "@/features/servers/utils/os-support";
import { cn } from "@/shared/lib/utils";

interface HostsSectionProps {
  institutionUid: string;
}

export function HostsSection({ institutionUid }: HostsSectionProps) {
  const { data: hosts = [], isLoading } = useHosts(institutionUid);
  const { create, update, remove } = useHostMutations(institutionUid);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IHost | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (host: IHost) => {
    setEditing(host);
    setDialogOpen(true);
  };

  const handleSubmit = async (data: CreateHostInput) => {
    try {
      if (editing) {
        await update.mutateAsync({ hostId: editing.id, data });
        toast.success("Host actualizado");
      } else {
        await create.mutateAsync(data);
        toast.success("Host creado");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al guardar host");
      throw error;
    }
  };

  const handleDelete = async (host: IHost) => {
    if (!window.confirm(`¿Eliminar el host "${host.name}"?`)) return;
    try {
      await remove.mutateAsync(host.id);
      toast.success("Host eliminado");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al eliminar");
    }
  };

  return (
    <Card className="gap-3 py-4 shadow-none">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 px-4 pb-0 border-b [.border-b]:pb-3">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <Server className="h-4 w-4 text-muted-foreground" aria-hidden />
            Hosts
            <Badge variant="secondary">{hosts.length}</Badge>
          </CardTitle>
          <CardDescription className="text-xs mt-1">
            Servidores físicos, VMs y estaciones. La verificación se hace desde la
            ficha.
          </CardDescription>
        </div>
        <Button size="sm" className="cursor-pointer shrink-0" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" aria-hidden />
          Host
        </Button>
      </CardHeader>
      <CardContent className="px-4 pt-3">
        {isLoading ? (
          <div className="flex justify-center py-8" role="status">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : hosts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Sin hosts. Agregá el primero según la infraestructura del centro.
          </p>
        ) : (
          <ul className="space-y-2">
            {hosts.map((host) => (
              <li
                key={host.id}
                className={cn(
                  "flex items-start justify-between gap-3 rounded-lg border border-l-4 p-3 transition-colors hover:bg-muted/20",
                  host.verifiedAt ? "border-l-emerald-500" : "border-l-amber-500"
                )}
              >
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Monitor
                      className="size-3.5 text-muted-foreground shrink-0"
                      aria-hidden
                    />
                    <p className="font-medium text-sm">{host.name}</p>
                    <Badge variant="secondary">{HOST_KIND_LABELS[host.kind]}</Badge>
                    {host.verifiedAt ? (
                      <Badge
                        variant="outline"
                        className="text-emerald-700 dark:text-emerald-400"
                      >
                        Verificado
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Sin verificar</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {[
                      host.ip,
                      host.hostname,
                      formatOsLabel(
                        host.osFamily,
                        resolveHostOsVersion(host) ?? undefined,
                        host.os
                      ) || host.os,
                      host.hardware,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Sin detalles"}
                  </p>
                  {host.anydeskId && (
                    <p className="text-xs font-mono text-muted-foreground">
                      AnyDesk: {host.anydeskId}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-9 cursor-pointer"
                    onClick={() => openEdit(host)}
                    aria-label={`Editar ${host.name}`}
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-9 cursor-pointer"
                    onClick={() => void handleDelete(host)}
                    disabled={remove.isPending}
                    aria-label={`Eliminar ${host.name}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <HostFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        host={editing}
        onSubmit={handleSubmit}
        isSaving={create.isPending || update.isPending}
      />
    </Card>
  );
}
